import { useEffect } from 'react';
import PropTypes from 'prop-types';

import Icon from '@/atoms/Icon/Icon';
import Text from '@/atoms/Text/Text';
import BlockRenderer from '@/global/BlockRenderer';

import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';

export default function StationDetailSidebar({
  assets,
  closeStationSidebar,
  entries,
  goToPreviousStation,
  goToNextStation,
  station,
  stationRef,
}) {
  // create half background for stationImage
  useEffect(() => {
    if (!stationRef?.current) return;

    const stationImage = stationRef?.current.querySelector(
      '.aaco-detail-station__body__half .aaco-story picture'
    );
    if (!stationImage?.clientHeight) return;
    const height = stationImage.clientHeight / 2;

    const greyBg = stationRef?.current.querySelector(
      '.aaco-detail-station__body__half:nth-child(1)'
    );
    greyBg.style.height = height;

    const whiteBg = stationRef?.current.querySelector(
      '.aaco-detail-station__body__half:nth-child(2)'
    );
    whiteBg.style.height = height;
  }, [stationRef]);

  return (
    <div
      className={`aaco-detail-station w-full md:w-1/2 mt-[40vh] md:mt-0 md:h-full relative md:absolute
      md:right-0 md:opacity-1 z-30 overflow-y-auto
      md:transition-[width] md:duration-500 md:ease-out bg-warm-grey mb-[55px] ${
        station ? '' : 'hidden md:block md:w-0 md:opacity-50'
      } `}
      ref={stationRef}
    >
      <div
        // force update
        key={station?.name}
        className="flex flex-col bg-warm-grey"
      >
        {/* START: Grey Parts */}
        <div
          className={`aaco-detail-station__body__grey w-full
          flex justify-between md:justify-start items-end flex-col
          md:transition-all md:duration-200 ${
            station ? 'bg-warm-grey' : 'bg-transparent'
          }
          `}
        >
          <div className="aaco-detail-station__close hidden md:flex mt-[53px] mb-[41px] md:md-[37px] mr-[55px]">
            <div className="cursor-pointer" onClick={closeStationSidebar}>
              <Icon name="close" className="w-6 h-6 text-acacia" />
            </div>
          </div>
          <div
            className={`aaco-detail-station__main-content w-full flex flex-col self-end text-black
          p-[30px] pt-[65px] md:pt-[0] md:px-[100px] min-h-[60vh] bg-warm-grey`}
          >
            <div className="aaco-station-detail__title mb-1 md:mb-6">
              <Text tag="h4" style="text-black hidden md:block animate-fade-in">
                {station?.name}
              </Text>
              <hr className="mt-4 border-black" />
              <Text tag="h5" style="text-acacia block md:hidden animate-fade-in">
                {station?.name}
              </Text>
              <div className="aaco-station-detail__location flex gap-x-2 md:gap-x-[7px] mt-[15px] md:mt-[22px] animate-fade-in">
                <Icon
                  className="text-black"
                  height={24}
                  name="location"
                  width={24}
                />
                <Text tag="body-large" style="text-black">{station?.location}</Text>

              </div>
            </div>
            <div className="aaco-station-detail__body flex flex-col gap-y-[30px] animate-fade-in">
              <ul>
                <li className="w-full flex items-start py-2.5 border-t border-black">
                  <Text tag="body" style="w-[172px] min-w-[172px]">
                    SIZE:
                  </Text>
                  <Text tag="body" style="uppercase text-black">{station?.size ?? '-'}</Text>
                </li>
                <li className="w-full flex items-start py-2.5 border-t border-black">
                  <Text tag="body" style="w-[172px] min-w-[172px]">
                    STAFF:
                  </Text>
                  <Text tag="body" style="uppercase text-black">{station?.staff ?? '-'}</Text>
                </li>
                <li className="w-full flex items-start py-2.5 border-t border-black">
                  <Text tag="body" style="w-[172px] min-w-[172px]">
                    PRIMARY PURPOSE:
                  </Text>
                  <Text tag="body" style="uppercase text-black">{station?.primary_purpose ?? '-'}</Text>
                </li>
                <li className="w-full flex items-start py-2.5 border-t border-black">
                  <Text tag="body" style="w-[172px] min-w-[172px]">
                    CATTLE:
                  </Text>
                  <Text tag="body" style="uppercase text-black">{station?.cattle ?? '-'}</Text>
                </li>
                <li className="w-full flex items-start py-2.5 border-y border-black">
                  <Text tag="body" style="w-[172px] min-w-[172px]">
                    TERRAIN:
                  </Text>
                  <Text tag="body" style="uppercase text-black">{station?.terrain ?? '-'}</Text>
                </li>
              </ul>
              <div className="aaco-station-detail__description">
                {station?.description && (
                  <BlockRenderer
                    assets={assets}
                    entries={entries}
                    blocks={[station.description]}
                    carouselContentStyle={{
                      hasBackground: true,
                      compactSpace: true,
                      linkStyle: 'link-invert pb-8',
                    }}
                    compactSpace
                    richTextStyle={{
                      paragraph: {
                        customFont: TEXT_BASE_CLASS['body'] + ' text-acacia',
                        blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                        paragraphWrapper: 'leading-5 mb-4',
                      },
                      list: {
                        paragraph: {
                          customFont: TEXT_BASE_CLASS['body'] + ' text-acacia',
                          blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                        },
                        ul: 'mb-4',
                      },
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        {/* END: Grey Parts */}

        {/* START: White Parts */}
        {station?.stationImage && (
          <div
            className={`aaco-detail-station__body__half relative animate-fade-in`}
          >
            <div className="absolute top-0 bg-warm-grey h-1/2 w-full"></div>
            <div className="absolute bottom-0 bg-warm-grey h-1/2 w-full"></div>
            <div
              className={`flex items-center justify-center container mx-auto md:px-[100px] ${
                !station?.content?.length && 'pb-[70px] md:pb-[100px]'
              }`}
            >
              <BlockRenderer
                assets={assets}
                entries={entries}
                blocks={[station.stationImage]}
                carouselContentStyle={{
                  hasBackground: true,
                  compactSpace: true,
                  linkStyle: 'link-invert pb-8',
                }}
                compactSpace
                richTextStyle={{
                  paragraph: {
                    customFont: TEXT_BASE_CLASS['body'],
                    blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                    paragraphWrapper: 'leading-5 mb-4',
                  },
                  list: {
                    paragraph: {
                      customFont: TEXT_BASE_CLASS['body'],
                      blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                    },
                    ul: 'mb-4',
                  },
                }}
              />
            </div>
          </div>
        )}
        {station?.content?.length > 0 && (
          <div className="aaco-detail-station__body__white bg-warm-grey">
            <div className="container mx-auto pt-[30px] md:px-[100px] text-black flex flex-col gap-y-[46px] md:gap-y-14 pb-[70px] md:pb-[100px] animate-fade-in">
              <BlockRenderer
                assets={assets}
                entries={entries}
                blocks={station?.content}
                carouselContentStyle={{
                  hasBackground: true,
                  compactSpace: true,
                  linkStyle: 'link-invert pb-8',
                }}
                compactSpace
                richTextStyle={{
                  paragraph: {
                    customFont: TEXT_BASE_CLASS['body'],
                    blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                    paragraphWrapper: 'leading-5 mb-4',
                  },
                  list: {
                    paragraph: {
                      customFont: TEXT_BASE_CLASS['body'],
                      blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                    },
                    ul: 'mb-4',
                  },
                }}
              />
            </div>
          </div>
        )}
        {/* END: White Parts */}

        {/* Footer */}
        <div className="aaco-detail-station__footer fixed h-[55px] w-full md:w-1/2 bottom-0 bg-warmer-grey text-white flex items-center justify-between z-30">
          <div
            className="flex items-center cursor-pointer"
            onClick={goToPreviousStation}
            role="button"
          >
            <div className="w-[40px] pl-3 mx-auto">
              <Icon
                className="mx-auto text-black"
                height={6.74}
                name="button-arrow-left"
                width={11.67}
              />
            </div>
            <Text
              tag="button"
              style="text-black hover:underline hover:underline-offset-[5px] hover:decoration-[1.5px] hover:decoration-black"
            >
              Previous Location
            </Text>
          </div>
          <div
            className={'flex items-center cursor-pointer'}
            role="button"
            onClick={goToNextStation}
          >
            <Text
              tag="button"
              style="text-black hover:underline hover:underline-offset-[5px] hover:decoration-[1.5px] hover:decoration-black"
            >
              Next Location
            </Text>
            <div className="w-[40px] pr-3">
              <Icon
                className="mx-auto text-black"
                name="button-arrow-right"
                height={6.74}
                width={11.67}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

StationDetailSidebar.propTypes = {
  station: PropTypes.shape({
    cattle: PropTypes.string,
    description: PropTypes.shape({
      content: PropTypes?.array,
    }),
    goToPreviousStation: PropTypes.func,
    goToNextStation: PropTypes.func,
    primary_purpose: PropTypes.string,
    stationRef: PropTypes.any,
    size: PropTypes.string,
    staff: PropTypes.number,
    terrain: PropTypes.string,
  }),
};
